<script lang="ts" setup>
import {ref, watch} from 'vue';
import {useDialogPluginComponent, useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';

const {t} = useI18n();
const api = useApi();
const $q = useQuasar();
const route = useRoute();

defineEmits([
  ...useDialogPluginComponent.emits,
]);

const {dialogRef, onDialogOK, onDialogCancel} = useDialogPluginComponent();

const loading = ref(false);

// Close dialog if user gets logged out (navigates away from profile page)
// This happens when deletion fails with 401 and auto-logout is triggered
watch(() => route.path, (newPath) => {
  if (!(newPath?.includes('/account/profile') ?? false)) {
    onDialogCancel();
  }
});

async function handleDelete(): Promise<void> {
  if (loading.value) {return;}

  loading.value = true;

  try {
    await api.me.deleteAccount();
    onDialogOK();
  } catch (error) {
    $q.notify({
      type: 'negative',
      message: extractError(error, t),
    });
  }
  // eslint-disable-next-line require-atomic-updates
  loading.value = false;
}
</script>

<template>
  <q-dialog
    ref="dialogRef"
    persistent
    transition-show="jump-up"
    transition-hide="jump-down"
  >
    <div class="modal-inner">
      <h2 class="dialog-title">
        {{ $t('delete_account') }}
      </h2>

      <p class="warning-message">
        {{ $t('delete_account_warning') }}
      </p>

      <div class="buttons">
        <balm-btn
          class="button"
          :label="$t('cancel')"
          theme="grey"
          paddings="p2-5"
          :disable="loading"
          @click="onDialogCancel"
        />
        <balm-btn
          class="button"
          paddings="p2-5"
          theme="red"
          :label="$t('delete')"
          :loading="loading"
          :disable="loading"
          @click="handleDelete"
        />
      </div>
    </div>
  </q-dialog>
</template>

<style lang="scss" scoped>
.modal-inner {
  display: flex;
  flex-direction: column;
  background-color: var(--Neutral-UI-neutral-ui-10);
  padding: var(--Spacing-spacing-5);
  border-radius: var(--card-border-radius);
  min-width: 300px;
  max-width: 480px;
}

.dialog-title {
  @include font-h3-bold;
  margin: 0 0 var(--Spacing-spacing-4) 0;
}

.warning-message {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-80);
  margin: 0 0 var(--Spacing-spacing-4) 0;
  line-height: 1.5;
}

.buttons {
  display: flex;
  gap: var(--Spacing-spacing-3);
  flex-direction: column-reverse;

  @include screen-sm {
    flex-direction: row;
    justify-content: flex-end;
  }
}

.button {
  width: 100%;
  @include screen-sm {
    width: auto;
  }
}
</style>
